import { useCallback, useState } from 'react';
import type { InsightEvent, InsightType, InsightValue } from '../types/insights';

const STORAGE_KEY = 'rekovelle-simulator-insights';

function load(): InsightEvent[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as InsightEvent[]) : [];
  } catch {
    return [];
  }
}

function persist(events: InsightEvent[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    // Storage unavailable — keep in-memory log only.
  }
}

/** Captures behavioural insight events (persisted locally; ready to forward to analytics). */
export function useInsightLog() {
  const [events, setEvents] = useState<InsightEvent[]>(load);

  const log = useCallback((type: InsightType, patientId: string, data: Record<string, InsightValue> = {}) => {
    const event: InsightEvent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type,
      patientId,
      at: new Date().toISOString(),
      data,
    };
    setEvents((prev) => {
      const next = [...prev, event];
      persist(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setEvents([]);
    persist([]);
  }, []);

  return { events, log, clear };
}
